import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PRIMITIVE_METADATA } from '../../config/primitiveMetadata';
import { PRIMITIVE_RELATIONSHIPS } from '../../config/primitiveRelationships';
import { GraphCanvas } from '../GraphCanvas';
import { useGraphDimensions } from '../shared/useGraphDimensions';
import { DOMAIN_CATALOG } from '@unwritten/domain-registry';
import { HelpCircle } from 'lucide-react';
/**
 * Get metadata for any domain (core or non-core)
 * Falls back to domain-registry data if not in PRIMITIVE_METADATA
 */
function getDomainMetadata(domainId) {
    // Try to get from core primitives first
    if (domainId in PRIMITIVE_METADATA) {
        return PRIMITIVE_METADATA[domainId];
    }
    // Fall back to domain catalog
    const domain = DOMAIN_CATALOG.find((d) => d.id === domainId);
    if (domain) {
        return {
            id: domain.id,
            name: domain.name,
            description: domain.description || '',
            color: '#94A3B8', // Neutral gray for non-core primitives
            icon: HelpCircle,
        };
    }
    // Ultimate fallback
    return {
        id: domainId,
        name: domainId,
        description: '',
        color: '#94A3B8',
        icon: HelpCircle,
    };
}
// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
            delayChildren: 0.05,
        },
    },
};
const statCardVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
            type: 'spring',
            stiffness: 300,
            damping: 24,
        },
    },
};
const schemaCardVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
        opacity: 1,
        x: 0,
        transition: {
            type: 'spring',
            stiffness: 300,
            damping: 30,
        },
    },
};
export function PrimitiveGraphTab({ primitiveType }) {
    const [selectedRelation, setSelectedRelation] = useState(null);
    const fgRef = useRef(null);
    const containerRef = useRef(null);
    const dimensions = useGraphDimensions(containerRef);
    const relationships = PRIMITIVE_RELATIONSHIPS[primitiveType];
    const primitive = PRIMITIVE_METADATA[primitiveType];
    const graphData = useMemo(() => {
        const nodes = [];
        const edges = [];
        nodes.push({
            id: primitiveType,
            type: primitiveType,
            label: primitive.name,
            color: primitive.color,
            size: 12,
        });
        const targetPrimitives = new Set();
        relationships.forEach((rel) => {
            targetPrimitives.add(rel.targetPrimitive);
        });
        targetPrimitives.forEach((target) => {
            const targetMeta = getDomainMetadata(target);
            nodes.push({
                id: target,
                type: target,
                label: targetMeta.name,
                color: targetMeta.color,
                size: 10,
            });
        });
        relationships.forEach((rel, idx) => {
            edges.push({
                id: `rel-${idx}`,
                source: primitiveType,
                target: rel.targetPrimitive,
                label: rel.field,
                fieldType: rel.fieldType,
            });
        });
        return { nodes, edges };
    }, [primitiveType, primitive, relationships]);
    const handleLinkClick = useCallback((link) => {
        const idx = parseInt(link.id.split('-')[1]);
        setSelectedRelation(idx);
    }, []);
    const handleSchemaCardClick = useCallback((idx) => {
        setSelectedRelation(idx);
        if (fgRef.current && graphData.edges[idx]) {
            const link = graphData.edges[idx];
            const sourceNode = graphData.nodes.find((n) => n.id === link.source);
            const targetNode = graphData.nodes.find((n) => n.id === link.target);
            if (sourceNode && targetNode) {
                const centerX = (sourceNode.x + targetNode.x) / 2;
                const centerY = (sourceNode.y + targetNode.y) / 2;
                fgRef.current.centerAt(centerX, centerY, 1000);
                fgRef.current.zoom(3, 1000);
            }
        }
    }, [graphData]);
    useEffect(() => {
        if (fgRef.current) {
            const fg = fgRef.current;
            fg.d3Force('charge')?.strength(-400);
            fg.d3Force('link')?.distance(100);
            fg.d3Force('center')?.strength(1);
            setTimeout(() => {
                fg.zoomToFit(400, 50);
            }, 500);
        }
    }, [graphData]);
    if (relationships.length === 0) {
        return (_jsxs(motion.div, { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] }, className: "text-center py-12 text-uw-text-tertiary", children: [_jsx("p", { className: "text-lg mb-2", children: "No relationships defined" }), _jsx("p", { className: "text-sm", children: "This primitive does not have foreign key relationships to other primitives." }), primitiveType === 'card' && (_jsx(motion.div, { initial: { opacity: 0, scale: 0.95 }, animate: { opacity: 1, scale: 1 }, transition: { delay: 0.2, duration: 0.3 }, className: "mt-4 p-4 bg-uw-info-surface border border-uw-info/20 rounded inline-block text-left", children: _jsxs("p", { className: "text-sm text-uw-info", children: [_jsx("strong", { children: "Note:" }), " Card is an ephemeral primitive used for runtime operations only. It processes data from Templates but does not persist relationships."] }) }))] }));
    }
    const targetPrimitives = new Set();
    relationships.forEach((rel) => {
        targetPrimitives.add(rel.targetPrimitive);
    });
    const relationshipTypeCount = relationships.reduce((acc, rel) => {
        acc[rel.relationshipType] = (acc[rel.relationshipType] || 0) + 1;
        return acc;
    }, {});
    return (_jsxs("div", { className: "space-y-6", children: [_jsxs(motion.div, { variants: containerVariants, initial: "hidden", animate: "visible", className: "grid grid-cols-1 md:grid-cols-3 gap-4", children: [_jsxs(motion.div, { variants: statCardVariants, whileHover: { scale: 1.02, y: -2 }, className: "p-4 bg-uw-surface-elevated border border-uw-border-default rounded-lg", children: [_jsx("div", { className: "text-sm text-uw-text-secondary", children: "Total Relationships" }), _jsx("div", { className: "text-2xl font-bold text-uw-text-primary", children: relationships.length })] }), _jsxs(motion.div, { variants: statCardVariants, whileHover: { scale: 1.02, y: -2 }, className: "p-4 bg-uw-surface-elevated border border-uw-border-default rounded-lg", children: [_jsx("div", { className: "text-sm text-uw-text-secondary", children: "Connected Primitives" }), _jsx("div", { className: "text-2xl font-bold text-uw-text-primary", children: targetPrimitives.size })] }), _jsxs(motion.div, { variants: statCardVariants, whileHover: { scale: 1.02, y: -2 }, className: "p-4 bg-uw-surface-elevated border border-uw-border-default rounded-lg", children: [_jsx("div", { className: "text-sm text-uw-text-secondary", children: "Primary Type" }), _jsx("div", { className: "text-sm font-medium text-uw-text-primary mt-1", children: Object.entries(relationshipTypeCount).sort((a, b) => b[1] - a[1])[0]?.[0] || '—' })] })] }), _jsx("div", { className: "grid grid-cols-1 lg:grid-cols-2 gap-6", children: _jsxs("div", { children: [_jsx(motion.h3, { initial: { opacity: 0, x: -20 }, animate: { opacity: 1, x: 0 }, transition: { delay: 0.2, duration: 0.4 }, className: "text-lg font-semibold mb-4 text-uw-text-primary", children: "Relationship Schema" }), _jsx(motion.div, { variants: containerVariants, initial: "hidden", animate: "visible", className: "space-y-3", children: _jsx(AnimatePresence, { mode: "popLayout", children: relationships.map((rel, idx) => {
                                    const targetMeta = getDomainMetadata(rel.targetPrimitive);
                                    const TargetIcon = targetMeta.icon;
                                    const isSelected = selectedRelation === idx;
                                    return (_jsxs(motion.div, { layout: true, variants: schemaCardVariants, onClick: () => handleSchemaCardClick(idx), whileHover: {
                                            scale: 1.02,
                                            y: -2,
                                            transition: {
                                                type: 'spring',
                                                stiffness: 400,
                                                damping: 25,
                                            },
                                        }, whileTap: { scale: 0.98 }, animate: {
                                            borderColor: isSelected
                                                ? 'var(--uw-interactive-primary)'
                                                : 'var(--uw-border-default)',
                                            backgroundColor: isSelected
                                                ? 'color-mix(in srgb, var(--uw-interactive-primary) 10%, transparent)'
                                                : 'transparent',
                                        }, transition: {
                                            layout: { type: 'spring', stiffness: 300, damping: 30 },
                                            backgroundColor: { duration: 0.2 },
                                            borderColor: { duration: 0.2 },
                                        }, className: "p-4 border rounded-lg cursor-pointer", style: {
                                            boxShadow: isSelected ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
                                        }, children: [_jsxs("div", { className: "flex items-start justify-between mb-2", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(motion.code, { layout: true, className: "text-sm font-mono bg-uw-surface-elevated px-2 py-1 rounded border border-uw-border-default text-uw-text-primary", children: rel.field }), _jsx("span", { className: "text-uw-text-tertiary", children: "\u2192" }), _jsxs(motion.div, { layout: true, className: "flex items-center gap-1", children: [_jsx(TargetIcon, { className: "w-4 h-4", style: { color: targetMeta.color } }), _jsx("span", { className: "font-medium", style: { color: targetMeta.color }, children: targetMeta.name })] })] }), _jsx(motion.span, { layout: true, className: "text-xs px-2 py-1 rounded-full", style: {
                                                            backgroundColor: `${targetMeta.color}20`,
                                                            color: targetMeta.color,
                                                        }, children: rel.relationshipType })] }), _jsx(motion.p, { layout: true, className: "text-sm text-uw-text-secondary mb-2", children: rel.description }), _jsxs(motion.div, { layout: true, className: "flex items-center gap-2", children: [_jsx("span", { className: "text-xs text-uw-text-tertiary", children: "Field Type:" }), _jsx("span", { className: "text-xs px-2 py-0.5 bg-uw-surface-elevated text-uw-text-primary rounded border border-uw-border-default", children: rel.fieldType })] })] }, idx));
                                }) }) }), _jsxs(motion.div, { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.4, duration: 0.4 }, className: "mt-6 p-4 bg-uw-info-surface border border-uw-info/20 rounded-lg", children: [_jsx("h4", { className: "text-sm font-semibold text-uw-info mb-2", children: "Relationship Types" }), _jsxs("div", { className: "space-y-2 text-sm text-uw-info", children: [_jsxs("div", { children: [_jsx("strong", { children: "foreign_key:" }), " Direct reference to another record"] }), _jsxs("div", { children: [_jsx("strong", { children: "array:" }), " Multiple references stored in an array field"] }), _jsxs("div", { children: [_jsx("strong", { children: "edge_table:" }), " Many-to-many relationship via junction table"] })] })] })] }) }), _jsxs(motion.div, { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.3, duration: 0.5, ease: [0.16, 1, 0.3, 1] }, children: [_jsx("h3", { className: "text-lg font-semibold mb-4 text-uw-text-primary", children: "Relationship Graph" }), _jsx(motion.div, { ref: containerRef, initial: { opacity: 0, scale: 0.98 }, animate: { opacity: 1, scale: 1 }, transition: { delay: 0.4, duration: 0.5, ease: [0.16, 1, 0.3, 1] }, className: "border border-uw-border-default rounded-lg bg-uw-surface-default overflow-hidden", style: { height: '600px', position: 'relative' }, children: _jsx(GraphCanvas, { ref: fgRef, graphData: graphData, width: dimensions.width, height: dimensions.height, selectedLink: selectedRelation, onLinkClick: handleLinkClick, enableOffScreenIndicators: true, enableLinkLabels: true }) }), _jsx(motion.div, { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { delay: 0.6, duration: 0.4 }, className: "mt-4 p-3 bg-uw-surface-elevated border border-uw-border-default rounded", children: _jsxs("p", { className: "text-xs text-uw-text-secondary", children: [_jsx("strong", { children: "Tip:" }), " Click on relationship cards or graph edges to highlight and zoom to the connection. Off-screen nodes are indicated by arrows at the viewport edges."] }) })] })] }));
}
